import React, { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Camera, Trash, Crop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/api";
import ProfilePhotoCropper from "@/components/ProfilePhotoCropper";

// Read the file into a raw data URL (for the cropper modal). No resize here -
// the cropper produces the final 400x400 JPEG when the user taps "Use this photo".
async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Could not read the image"));
    r.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "", role_title: user.role_title || "", org: user.org || "",
    bio: user.bio || "", photo_url: user.photo_url || "",
    networking_opt_in: !!user.networking_opt_in,
  });
  const [saving, setSaving] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null);  // raw data URL while cropping
  const fileInputRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    try {
      const raw = await fileToDataUrl(file);
      setCropperSrc(raw);  // opens the crop dialog
    } catch (err) {
      toast.error(err.message || "Could not read the image");
    } finally {
      e.target.value = "";
    }
  };

  const onCropApply = (croppedDataUrl) => {
    setForm((f) => ({ ...f, photo_url: croppedDataUrl }));
    setCropperSrc(null);
    toast.success("Photo ready - tap Save to keep it");
  };
  const onCropCancel = () => setCropperSrc(null);

  const removePhoto = () => setForm((f) => ({ ...f, photo_url: "" }));

  const save = async () => {
    setSaving(true);
    try { await updateProfile(form); toast.success("Profile saved"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  // Initials fallback for the avatar when there's no photo.
  const initials = (form.name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join("");

  return (
    <div data-testid="profile-page" className="space-y-4">
      <div>
        <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1">Your profile</div>
        <h1 className="font-display text-3xl tracking-tight">Profile</h1>
      </div>

      <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm space-y-4">
        {/* Photo upload */}
        <div className="flex items-center gap-4">
          <div className="relative">
            {form.photo_url ? (
              <img
                src={form.photo_url}
                alt={form.name}
                className="w-20 h-20 rounded-full object-cover border-2 border-[#E5E9F0]"
                data-testid="profile-photo-preview"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full bg-[#E6F8FC] grid place-items-center text-[#000000] font-display text-2xl border-2 border-[#E5E9F0]"
                data-testid="profile-photo-placeholder"
              >
                {initials}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPhotoChange}
              className="hidden"
              data-testid="profile-photo-input"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="h-10 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                data-testid="profile-photo-choose-btn"
              >
                <Camera size={16} className="mr-1.5" />
                {form.photo_url ? "Change photo" : "Choose photo"}
              </Button>
              {form.photo_url && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCropperSrc(form.photo_url)}
                    className="h-10 rounded-sm"
                    data-testid="profile-photo-adjust-btn"
                  >
                    <Crop size={16} className="mr-1.5" /> Adjust
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={removePhoto}
                    className="h-10 rounded-sm"
                    data-testid="profile-photo-remove-btn"
                  >
                    <Trash size={16} className="mr-1.5" /> Remove
                  </Button>
                </>
              )}
            </div>
            <p className="text-xs text-[#7A828C] mt-2">You can crop and zoom after choosing.</p>
          </div>
        </div>

        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" value={form.name} onChange={update("name")} className="h-12 rounded-sm mt-1" data-testid="profile-name" />
        </div>
        <div>
          <Label htmlFor="role_title">Role</Label>
          <Input id="role_title" value={form.role_title} onChange={update("role_title")} className="h-12 rounded-sm mt-1" data-testid="profile-role" />
        </div>
        <div>
          <Label htmlFor="org">Organisation</Label>
          <Input id="org" value={form.org} onChange={update("org")} className="h-12 rounded-sm mt-1" data-testid="profile-org" />
        </div>
        <div>
          <Label htmlFor="bio">Bio</Label>
          <Textarea id="bio" value={form.bio} onChange={update("bio")} rows={3} className="rounded-sm mt-1" data-testid="profile-bio" />
        </div>
        <div className="flex items-center justify-between pt-2">
          <div>
            <Label>Allow other delegates to message me</Label>
            <p className="text-xs text-[#7A828C]">On by default. Turn off to hide your profile from the directory and block new DMs.</p>
          </div>
          <Switch checked={form.networking_opt_in} onCheckedChange={(v) => setForm((f) => ({ ...f, networking_opt_in: v }))} data-testid="profile-networking-switch" />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="flex-1 h-12 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="profile-save-btn">
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button onClick={logout} variant="outline" className="h-12 rounded-sm" data-testid="profile-logout-btn">Sign out</Button>
      </div>

      <ProfilePhotoCropper
        open={!!cropperSrc}
        imageDataUrl={cropperSrc}
        onApply={onCropApply}
        onCancel={onCropCancel}
      />
    </div>
  );
}
